import * as React from 'react';
import { InputEmail } from 'components/form';
import { useForm } from 'react-hook-form';
import { Typography, Button, ErrorMessageInput } from 'components/common';
import { authService } from '@/api';
import { useNotification } from '@/hooks';
import AppRoutes from '@/const/routes';
import { useRouter } from 'next/router';
import { setLocalStorage } from '@/utils/localStorage';
import { AuthLayout } from '@/components/layout/layout/AuthLayout';
import { ResetCard } from '@/components/auth/ResetCard';

type CodeStatusVerification = 'unverified' | 'verified';

export const ForgotPasswordScreen = (): JSX.Element => {
	const router = useRouter();
	const { pathname } = router;
	const { toastSuccess, toastError } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [statusVerification, setStatusVerification] =
		React.useState<CodeStatusVerification>('unverified');
	const [errorMessage, setErrorMessage] = React.useState<string>('');
	const [userEmail, setUserEmail] = React.useState<string>('');
	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isDirty, isValid },
	} = useForm({ mode: 'onSubmit' });

	const titlePage: Record<CodeStatusVerification, string> = {
		unverified: 'Forgot your password?',
		verified: 'Check your email',
	};

	// Inputs rules
	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
			// validate: (value: string) => value || errorMessage,
		},
	};

	const handleBack = () => {
		statusVerification === 'verified'
			? setStatusVerification('unverified')
			: pathname.includes('/lawyerapp')
			? router.push(AppRoutes.LAWYERAPP_AUTH_SIGN_IN)
			: router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
	};

	const handleSubmitDataForm = async (data: any): Promise<void> => {
		setIsLoading(true);
		setErrorMessage('');
		try {
			setUserEmail(data.email);
			await authService.forgotPassword(data.email);
			setStatusVerification('verified');
			setLocalStorage('user_email', data.email);
			toastSuccess('Password reset email successfully sent.');
		} catch (error: any) {
			if (error.statusCode === 500) {
				toastError('An error has occurred. Please try again');
			} else if (error.statusCode === 404) {
				setErrorMessage('Email not found. Please double-check your entry.');
			} else {
				setErrorMessage(error.message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<AuthLayout withCarrousel={false}>
			<ResetCard
				title={titlePage[statusVerification]}
				srcImage={
					statusVerification === 'verified'
						? '/img/landing/svg/white-boy-alt.svg'
						: undefined
				}
				handleBack={handleBack}
			>
				<form
					className="w-full space-y-8"
					onSubmit={handleSubmit(handleSubmitDataForm)}
				>
					{statusVerification === 'verified' ? (
						<>
							<Typography
								type="regular-18"
								className="text-center text-opacity-black"
							>
								{`We have sent a verification email to `} {userEmail}. <br />
								{` Don’t forget to check your Spam folder.`}
							</Typography>
							{/* Resend */}
							<div className="flex flex-col sm:flex-row justify-center items-center gap-2 md:gap-1 mt-10">
								<Typography
									type="regular-16"
									className="text-opacity-black"
								>{`Didn’t get the email? `}</Typography>
								<button type="submit" className="border-none">
									<Typography
										type="semibold-16"
										className="text-primary hover:opacity-60"
									>
										{' '}
										Resend Password Reset Email
									</Typography>
								</button>
							</div>
						</>
					) : (
						<>
							<div className="mb-10">
								<InputEmail
									name="email"
									title="Email"
									register={register}
									rules={rules.email}
									error={errors.email}
									onChangeCustom={() => setErrorMessage('')}
									setValueCustom={setValue}
								/>
								{errorMessage && <ErrorMessageInput message={errorMessage} />}
							</div>
							<Button
								type="submit"
								size="fit"
								decoration="primary"
								disabled={isLoading || !isDirty || !isValid}
								label="Reset Password"
								className="mx-auto !w-[300px] !h-16"
							/>
						</>
					)}
				</form>
			</ResetCard>
		</AuthLayout>
	);
};
